import type { BuildingGraph, BuildingGraphPlanarTopology } from "building-types/building/buildingGraphTypes";
import type { Building } from "building-types/building/buildingTypes";
import {
  isAbsoluteVerticalDatum,
  isWallPlaneHorizontalDatum,
  type DatumEntity,
} from "building-types/datums/datumEntityTypes";
import type { Doc } from "building-types/doc";
import type { BuildingGraphPlanarTopologyHandle } from "building-types/entities/buildingEntityHandle";
import type { PlanarTopologyDatumBounds } from "building-types/walls2/planarTopologyDatumBoundsTypes";
import { planarTopologyDatumBoundsHasDatums } from "building-types/walls2/planarTopologyDatumBoundsTypes";
import { quantizePlanarTopologyRelativeInset } from "building-types/walls2/planarTopologyRelativeInset";
import type {
  WallPlaneOperationAABB,
  WallPlaneOperationAABBHorizontalSideValue,
  WallPlaneOperationAABBVerticalSideValue,
  WallPlaneOperationBoundary,
} from "building-types/walls2/wallPlane";
import { getWallPlaneOperationAABB } from "building-types/walls2/wallPlane";
import {
  buildSplitRegionOperationBoundary,
  operationAABBMatchesDatumBounds,
  planeUVAABBToOperationAABB,
  resolveWallPlaneOperationAABBToUVAABB,
} from "building-types/walls2/wallPlaneOperationAABB";
import type { Table } from "common/data/table";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON } from "geom/core";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { PlanarTopologyAssemblyRegion } from "./planarTopologyAssemblyRegions";
import { resolveSplitRegionUBounds, resolveSplitRegionVBounds } from "./planarTopologySplitRegionBounds";
import { worldZToTopologyV } from "./planarTopologyVerticalDatumProjection";

export interface PlanarTopologyRegionUVAABB {
  readonly uMin: number;
  readonly uMax: number;
  readonly vMin: number;
  readonly vMax: number;
}

function regionAabbFromAssemblyRegion(region: PlanarTopologyAssemblyRegion): AABB2 {
  return {
    minX: region.uMin,
    minY: region.vMin,
    maxX: region.uMax,
    maxY: region.vMax,
  };
}

function findHorizontalDatumAtU(
  u: number,
  topology: BuildingGraphPlanarTopology,
  datums: Table<DatumEntity>,
): PlanarTopologyDatumBounds["startHorizontalDatumId"] | undefined {
  for (const datumId in datums) {
    const datum = datums[datumId];
    if (
      isWallPlaneHorizontalDatum(datum) &&
      datum.planeHash === topology.planeHash &&
      datum.offset !== undefined &&
      Math.abs(datum.offset - u) <= DIST_EPSILON
    ) {
      return datum.id;
    }
  }
  return undefined;
}

function findVerticalDatumAtV(
  v: number,
  topology: BuildingGraphPlanarTopology,
  datums: Table<DatumEntity>,
): PlanarTopologyDatumBounds["startVerticalDatumId"] | undefined {
  for (const datumId in datums) {
    const datum = datums[datumId];
    if (!isAbsoluteVerticalDatum(datum) || datum.offset === undefined) {
      continue;
    }
    if (Math.abs(worldZToTopologyV(topology, datum.offset) - v) <= DIST_EPSILON) {
      return datum.id;
    }
  }
  return undefined;
}

/**
 * Snap region edges to horizontal/vertical datums when they are not on the plane outline.
 * Returns undefined when any interior edge cannot snap (caller falls back to relative insets),
 * or when the region matches the full plane outline with no datum edges.
 */
function trySnapDatumBoundsForRegionAabb(
  regionAabb: AABB2,
  topology: BuildingGraphPlanarTopology,
  planeOutlineAabb: AABB2,
  datums: Table<DatumEntity>,
): PlanarTopologyDatumBounds | undefined {
  let startHorizontalDatumId: PlanarTopologyDatumBounds["startHorizontalDatumId"];
  let endHorizontalDatumId: PlanarTopologyDatumBounds["endHorizontalDatumId"];
  let startVerticalDatumId: PlanarTopologyDatumBounds["startVerticalDatumId"];
  let endVerticalDatumId: PlanarTopologyDatumBounds["endVerticalDatumId"];

  if (Math.abs(regionAabb.minX - planeOutlineAabb.minX) > DIST_EPSILON) {
    startHorizontalDatumId = findHorizontalDatumAtU(regionAabb.minX, topology, datums);
    if (startHorizontalDatumId === undefined) {
      return undefined;
    }
  }

  if (Math.abs(regionAabb.maxX - planeOutlineAabb.maxX) > DIST_EPSILON) {
    endHorizontalDatumId = findHorizontalDatumAtU(regionAabb.maxX, topology, datums);
    if (endHorizontalDatumId === undefined) {
      return undefined;
    }
  }

  if (Math.abs(regionAabb.minY - planeOutlineAabb.minY) > DIST_EPSILON) {
    startVerticalDatumId = findVerticalDatumAtV(regionAabb.minY, topology, datums);
    if (startVerticalDatumId === undefined) {
      return undefined;
    }
  }

  if (Math.abs(regionAabb.maxY - planeOutlineAabb.maxY) > DIST_EPSILON) {
    endVerticalDatumId = findVerticalDatumAtV(regionAabb.maxY, topology, datums);
    if (endVerticalDatumId === undefined) {
      return undefined;
    }
  }

  const bounds: PlanarTopologyDatumBounds = {
    ...(startHorizontalDatumId !== undefined ? { startHorizontalDatumId } : {}),
    ...(endHorizontalDatumId !== undefined ? { endHorizontalDatumId } : {}),
    ...(startVerticalDatumId !== undefined ? { startVerticalDatumId } : {}),
    ...(endVerticalDatumId !== undefined ? { endVerticalDatumId } : {}),
  };

  if (!planarTopologyDatumBoundsHasDatums(bounds)) {
    return undefined;
  }

  return bounds;
}

/**
 * Annotate relative-inset handles with any datums that land on region edges, even when other
 * edges (e.g. floor-clip V) cannot snap. Keeps selection handles datum-aware for Edit Wall.
 */
function collectPartialDatumBoundsAtEdges(
  regionAabb: AABB2,
  topology: BuildingGraphPlanarTopology,
  datums: Table<DatumEntity>,
): PlanarTopologyDatumBounds {
  const startHorizontalDatumId = findHorizontalDatumAtU(regionAabb.minX, topology, datums);
  const endHorizontalDatumId = findHorizontalDatumAtU(regionAabb.maxX, topology, datums);
  const startVerticalDatumId = findVerticalDatumAtV(regionAabb.minY, topology, datums);
  const endVerticalDatumId = findVerticalDatumAtV(regionAabb.maxY, topology, datums);

  return {
    ...(startHorizontalDatumId !== undefined ? { startHorizontalDatumId } : {}),
    ...(endHorizontalDatumId !== undefined ? { endHorizontalDatumId } : {}),
    ...(startVerticalDatumId !== undefined ? { startVerticalDatumId } : {}),
    ...(endVerticalDatumId !== undefined ? { endVerticalDatumId } : {}),
  };
}

function relativeInsetFromHorizontalSide(side: WallPlaneOperationAABBHorizontalSideValue | undefined): number {
  if (side?.mode === "RELATIVE") {
    return side.value;
  }
  return 0;
}

function relativeInsetFromVerticalSide(side: WallPlaneOperationAABBVerticalSideValue | undefined): number {
  if (side?.mode === "RELATIVE") {
    return side.value;
  }
  return 0;
}

export function planarTopologyHandleWithAssemblyRegion(
  topologyId: string,
  region: PlanarTopologyAssemblyRegion,
  planeOutlineAabb: AABB2,
  topology: BuildingGraphPlanarTopology,
  datums: Table<DatumEntity> = {},
): BuildingGraphPlanarTopologyHandle {
  const regionAabb = regionAabbFromAssemblyRegion(region);
  const datumBounds = trySnapDatumBoundsForRegionAabb(regionAabb, topology, planeOutlineAabb, datums);
  if (datumBounds) {
    const handle: BuildingGraphPlanarTopologyHandle = {
      entityType: "BUILDING_GRAPH_PLANAR_TOPOLOGY",
      id: topologyId,
      ...(datumBounds.startHorizontalDatumId !== undefined
        ? { startHorizontalDatumId: datumBounds.startHorizontalDatumId }
        : {}),
      ...(datumBounds.endHorizontalDatumId !== undefined
        ? { endHorizontalDatumId: datumBounds.endHorizontalDatumId }
        : {}),
      ...(datumBounds.startVerticalDatumId !== undefined
        ? { startVerticalDatumId: datumBounds.startVerticalDatumId }
        : {}),
      ...(datumBounds.endVerticalDatumId !== undefined ? { endVerticalDatumId: datumBounds.endVerticalDatumId } : {}),
    };
    return handle;
  }

  const partialDatums = collectPartialDatumBoundsAtEdges(regionAabb, topology, datums);
  const relativeAabb = planeUVAABBToOperationAABB(regionAabb, planeOutlineAabb);
  const handle: BuildingGraphPlanarTopologyHandle = {
    entityType: "BUILDING_GRAPH_PLANAR_TOPOLOGY",
    id: topologyId,
    ...(partialDatums.startHorizontalDatumId !== undefined
      ? { startHorizontalDatumId: partialDatums.startHorizontalDatumId }
      : {}),
    ...(partialDatums.endHorizontalDatumId !== undefined
      ? { endHorizontalDatumId: partialDatums.endHorizontalDatumId }
      : {}),
    ...(partialDatums.startVerticalDatumId !== undefined
      ? { startVerticalDatumId: partialDatums.startVerticalDatumId }
      : {}),
    ...(partialDatums.endVerticalDatumId !== undefined ? { endVerticalDatumId: partialDatums.endVerticalDatumId } : {}),
    relativeUStartInset: quantizePlanarTopologyRelativeInset(relativeInsetFromHorizontalSide(relativeAabb.start)),
    relativeUEndInset: quantizePlanarTopologyRelativeInset(relativeInsetFromHorizontalSide(relativeAabb.end)),
    relativeVBottomInset: quantizePlanarTopologyRelativeInset(relativeInsetFromVerticalSide(relativeAabb.bottom)),
    relativeVTopInset: quantizePlanarTopologyRelativeInset(relativeInsetFromVerticalSide(relativeAabb.top)),
  };
  return handle;
}

export function planarTopologyHandleHasRelativeInsets(handle: BuildingGraphPlanarTopologyHandle): boolean {
  return (
    handle.relativeUStartInset !== undefined ||
    handle.relativeUEndInset !== undefined ||
    handle.relativeVBottomInset !== undefined ||
    handle.relativeVTopInset !== undefined
  );
}

export function resolveAssemblyRegionUVAABBFromHandle(
  handle: BuildingGraphPlanarTopologyHandle,
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  doc: Doc,
  planeOutlineAabb: AABB2,
  planLevelIds?: ReadonlySet<string>,
): PlanarTopologyRegionUVAABB | undefined {
  // Relative insets are the precise assembly-band UV. Prefer them when present so hybrid
  // handles (partial datum IDs + relative insets for floor-clip edges) resolve correctly.
  if (planarTopologyHandleHasRelativeInsets(handle)) {
    const operationAabb: WallPlaneOperationAABB = {
      ...(handle.relativeUStartInset !== undefined
        ? { start: { mode: "RELATIVE", value: handle.relativeUStartInset } }
        : {}),
      ...(handle.relativeUEndInset !== undefined ? { end: { mode: "RELATIVE", value: handle.relativeUEndInset } } : {}),
      ...(handle.relativeVBottomInset !== undefined
        ? { bottom: { mode: "RELATIVE", value: handle.relativeVBottomInset } }
        : {}),
      ...(handle.relativeVTopInset !== undefined ? { top: { mode: "RELATIVE", value: handle.relativeVTopInset } } : {}),
    };

    const resolved = resolveWallPlaneOperationAABBToUVAABB(
      operationAabb,
      topology.planeHash,
      planeOutlineAabb,
      {},
      buildingGraph,
    );
    if (resolved) {
      return {
        uMin: resolved.minX,
        uMax: resolved.maxX,
        vMin: resolved.minY,
        vMax: resolved.maxY,
      };
    }
  }

  const datumBounds = {
    ...(handle.startHorizontalDatumId !== undefined ? { startHorizontalDatumId: handle.startHorizontalDatumId } : {}),
    ...(handle.endHorizontalDatumId !== undefined ? { endHorizontalDatumId: handle.endHorizontalDatumId } : {}),
    ...(handle.startVerticalDatumId !== undefined ? { startVerticalDatumId: handle.startVerticalDatumId } : {}),
    ...(handle.endVerticalDatumId !== undefined ? { endVerticalDatumId: handle.endVerticalDatumId } : {}),
  };

  if (planarTopologyDatumBoundsHasDatums(datumBounds)) {
    const uBounds = resolveSplitRegionUBounds(topology, doc, datumBounds, buildingGraph, planLevelIds);
    const vBounds = resolveSplitRegionVBounds(topology, doc, datumBounds);
    if (uBounds && vBounds) {
      return { uMin: uBounds.uMin, uMax: uBounds.uMax, vMin: vBounds.vMin, vMax: vBounds.vMax };
    }
  }

  return undefined;
}

export function buildAssemblyRegionOperationBoundary(
  regionAabb: AABB2,
  planeOutlineAabb: AABB2,
  topology: BuildingGraphPlanarTopology,
  datums: Table<DatumEntity> = {},
): WallPlaneOperationBoundary | undefined {
  const datumBounds = trySnapDatumBoundsForRegionAabb(regionAabb, topology, planeOutlineAabb, datums);
  if (datumBounds) {
    return buildSplitRegionOperationBoundary(datumBounds, datums);
  }

  const relativeAabb = planeUVAABBToOperationAABB(regionAabb, planeOutlineAabb);
  return { boundaryType: "AABB", aabb: relativeAabb };
}

function uvaabbsMatch(a: PlanarTopologyRegionUVAABB, b: PlanarTopologyRegionUVAABB): boolean {
  return (
    Math.abs(a.uMin - b.uMin) <= DIST_EPSILON &&
    Math.abs(a.uMax - b.uMax) <= DIST_EPSILON &&
    Math.abs(a.vMin - b.vMin) <= DIST_EPSILON &&
    Math.abs(a.vMax - b.vMax) <= DIST_EPSILON
  );
}

export function operationAABBMatchesAssemblyRegionAabb(
  operationBoundary: WallPlaneOperationBoundary | undefined,
  regionAabb: AABB2,
  planeOutlineAabb: AABB2,
  topology: BuildingGraphPlanarTopology,
  buildingGraph: BuildingGraph,
  datums: Table<DatumEntity> = {},
): boolean {
  const expectedBoundary = buildAssemblyRegionOperationBoundary(regionAabb, planeOutlineAabb, topology, datums);
  if (!expectedBoundary && !operationBoundary) {
    return true;
  }
  if (!expectedBoundary || !operationBoundary) {
    return false;
  }

  const expectedAabb = getWallPlaneOperationAABB(expectedBoundary);
  const ruleAabb = getWallPlaneOperationAABB(operationBoundary);
  if (!expectedAabb || !ruleAabb) {
    return false;
  }

  const expectedResolved = resolveWallPlaneOperationAABBToUVAABB(
    expectedAabb,
    topology.planeHash,
    planeOutlineAabb,
    {},
    buildingGraph,
  );
  const ruleResolved = resolveWallPlaneOperationAABBToUVAABB(
    ruleAabb,
    topology.planeHash,
    planeOutlineAabb,
    {},
    buildingGraph,
  );
  if (!expectedResolved || !ruleResolved) {
    return false;
  }

  return uvaabbsMatch(
    {
      uMin: expectedResolved.minX,
      uMax: expectedResolved.maxX,
      vMin: expectedResolved.minY,
      vMax: expectedResolved.maxY,
    },
    {
      uMin: ruleResolved.minX,
      uMax: ruleResolved.maxX,
      vMin: ruleResolved.minY,
      vMax: ruleResolved.maxY,
    },
  );
}

function planeOutlineAabbForTopology(
  buildingGraph: BuildingGraph,
  topology: BuildingGraphPlanarTopology,
): AABB2 | undefined {
  const planeLoop = buildingGraph.buildingPlanes?.[topology.planeHash]?.planeFace.loops[0];
  if (!planeLoop) {
    return undefined;
  }
  return polyLine2AABB2(planeLoop);
}

export function resolveAssemblyRegionOperationBoundaryFromHandle(
  building: Building,
  doc: Doc,
  topologyId: string,
  handle: BuildingGraphPlanarTopologyHandle,
  planLevelIds?: ReadonlySet<string>,
): WallPlaneOperationBoundary | undefined {
  const topology = building.buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return undefined;
  }

  const datumBounds = {
    ...(handle.startHorizontalDatumId !== undefined ? { startHorizontalDatumId: handle.startHorizontalDatumId } : {}),
    ...(handle.endHorizontalDatumId !== undefined ? { endHorizontalDatumId: handle.endHorizontalDatumId } : {}),
    ...(handle.startVerticalDatumId !== undefined ? { startVerticalDatumId: handle.startVerticalDatumId } : {}),
    ...(handle.endVerticalDatumId !== undefined ? { endVerticalDatumId: handle.endVerticalDatumId } : {}),
  };

  // Pure datum handles (no relative insets) keep datum-authored boundaries. Hybrid handles that
  // carry relative insets for floor-clip edges resolve via the assembly-region UV path below.
  if (planarTopologyDatumBoundsHasDatums(datumBounds) && !planarTopologyHandleHasRelativeInsets(handle)) {
    return buildSplitRegionOperationBoundary(datumBounds, doc.datums);
  }

  const planeOutlineAabb = planeOutlineAabbForTopology(building.buildingGraph, topology);
  if (!planeOutlineAabb) {
    return undefined;
  }

  const regionAabb = resolveAssemblyRegionUVAABBFromHandle(
    handle,
    topology,
    building.buildingGraph,
    doc,
    planeOutlineAabb,
    planLevelIds,
  );
  if (!regionAabb) {
    return undefined;
  }

  return buildAssemblyRegionOperationBoundary(
    {
      minX: regionAabb.uMin,
      minY: regionAabb.vMin,
      maxX: regionAabb.uMax,
      maxY: regionAabb.vMax,
    },
    planeOutlineAabb,
    topology,
    doc.datums,
  );
}

export function operationBoundaryMatchesTopologyHandle(
  operationBoundary: WallPlaneOperationBoundary | undefined,
  building: Building,
  doc: Doc,
  topologyId: string,
  handle: BuildingGraphPlanarTopologyHandle,
  datumBounds: PlanarTopologyDatumBounds = {},
  planLevelIds?: ReadonlySet<string>,
): boolean {
  if (planarTopologyDatumBoundsHasDatums(datumBounds)) {
    return operationAABBMatchesDatumBounds(operationBoundary, datumBounds, doc.datums);
  }

  const topology = building.buildingGraph.faceTopologies[topologyId];
  if (!topology) {
    return false;
  }

  const planeOutlineAabb = planeOutlineAabbForTopology(building.buildingGraph, topology);
  if (!planeOutlineAabb) {
    return false;
  }

  const regionAabb = resolveAssemblyRegionUVAABBFromHandle(
    handle,
    topology,
    building.buildingGraph,
    doc,
    planeOutlineAabb,
    planLevelIds,
  );
  if (!regionAabb) {
    return operationBoundary === undefined;
  }

  return operationAABBMatchesAssemblyRegionAabb(
    operationBoundary,
    {
      minX: regionAabb.uMin,
      minY: regionAabb.vMin,
      maxX: regionAabb.uMax,
      maxY: regionAabb.vMax,
    },
    planeOutlineAabb,
    topology,
    building.buildingGraph,
    doc.datums,
  );
}
